import * as yup from "yup";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// ─── Helpers ────────────────────────────────────────────────────────────────

const isGreaterType = (type) => type === "greater" || type === "greater_than";

const isLessType = (type) =>
  type === "less" || type === "less_than" || type === "lessthan";

/**
 * Sanitise a regex pattern string that may have been saved with surrounding
 * slash delimiters, quote characters, or regex flags attached — e.g. the API
 * can return  "^(A|B|AB|O)[+-]$/"  (note the trailing slash+quote).
 *
 * Handles all common forms:
 *   "^pattern$"      → ^pattern$   (double-quote wrapped)
 *   '^pattern$'      → ^pattern$   (single-quote wrapped)
 *   /^pattern$/      → ^pattern$   (slash-delimited, no flags)
 *   /^pattern$/gi    → ^pattern$   (slash-delimited with flags)
 *   "^pattern$/\"    → ^pattern$   (mixed — the actual broken form from the API)
 */
const sanitiseRegexPattern = (raw) => {
  if (!raw) return null;
  let p = raw.trim();
  // Strip any wrapping double or single quotes from both ends
  p = p.replace(/^["']|["']$/g, "").trim();
  // Strip a leading slash delimiter
  if (p.startsWith("/")) p = p.slice(1);
  // Strip a trailing  /flags  (e.g.  /  or  /gi  or  /m)
  p = p.replace(/\/[gimsuy]*$/, "");
  return p;
};

const buildDependencyTest = (parse, dependency) =>
  function (value) {
    if (!value) return true;

    const current = parse(value);
    if (!current || !current.isValid()) return true;

    const dependentValue = this.parent[dependency.dependency_uuid];
    if (!dependentValue) return true;

    const other = parse(dependentValue);
    if (!other || !other.isValid()) return true;

    const label = dependency.dependency_label || "the dependent field";

    if (isGreaterType(dependency.type)) {
      return current.isAfter(other)
        ? true
        : this.createError({ message: `Must be greater than ${label}` });
    }

    if (isLessType(dependency.type)) {
      return current.isBefore(other)
        ? true
        : this.createError({ message: `Must be less than ${label}` });
    }

    return true;
  };

// ─── Main export ────────────────────────────────────────────────────────────

export const generateValidationSchema = (sections = []) => {
  const schemaObject = {};

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      const { input_uuid, label, property } = field;
      const { general, field_type } = property;

      let validator = yup.string().trim();

      // ── Required ─────────────────────────────────────────────────────────
      if (general?.required) {
        validator = validator.required(`${label} is required`);
      }

      // ── Input Text ───────────────────────────────────────────────────────
      if (field_type === "Input Text") {
        const t = property.input_text || {};

        if (t.min_limit) {
          validator = validator.min(
            t.min_limit_range,
            `Minimum ${t.min_limit_range} characters required`,
          );
        }
        if (t.max_limit) {
          validator = validator.max(
            t.max_limit_range,
            `Maximum ${t.max_limit_range} characters allowed`,
          );
        }
        if (t.restrict_number) {
          validator = validator.matches(/^[^0-9]*$/, "Numbers are not allowed");
        }
        if (t.restrict_spl_char) {
          validator = validator.matches(
            /^[A-Za-z\s]*$/,
            "Special characters are not allowed",
          );
        }
        if (t.restrict_email) {
          validator = validator.test(
            "not-email",
            "Email is not allowed",
            (value) => {
              if (!value) return true;
              return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            },
          );
        }
      }

      // ── Input Email ──────────────────────────────────────────────────────
      if (field_type === "Input Email") {
        validator = validator.email("Invalid email address");
      }

      // ── Custom regex validations ─────────────────────────────────────────
      (property.validations || []).forEach((validation, vIndex) => {
        if (validation.validation_type_name === "Custom") {
          // Sanitise before building the RegExp — the API sometimes saves the
          // pattern with surrounding slash delimiters and/or quote characters
          // (e.g. "^(A|B|AB|O)[+-]$/") which make new RegExp() produce a
          // completely wrong pattern that never matches anything valid, so the
          // error never clears even after the user types a correct value.
          const cleaned = sanitiseRegexPattern(validation.regex_pattern);
          if (!cleaned) return;

          try {
            const regex = new RegExp(cleaned);
            // Use a unique test name per validation so multiple custom rules
            // on the same field don't silently overwrite each other.
            validator = validator.test(
              `custom-regex-${validation.id ?? vIndex}`,
              validation.message || "Invalid format",
              (value) => (!value ? true : regex.test(value)),
            );
          } catch {
            console.error(
              "Invalid regex pattern after sanitising:",
              validation.regex_pattern,
              "→",
              cleaned,
            );
          }
        }
      });

      // ── Input Current Time — dependency ──────────────────────────────────
      if (field_type === "Input Current Time") {
        const timeProps = property.input_current_time || {};
        const dependency = timeProps.dependency || {};

        if (dependency.dependency_uuid && dependency.type) {
          // FIX 3: Support both 12-hour and 24-hour formats.
          // Try the configured format first, then fall back to the other one
          // so a value like "03:00 AM" always parses correctly regardless of
          // what time_format the field declares.
          const timeFmt =
            timeProps.time_format === "24_hour" ? "HH:mm" : "hh:mm A";
          const altFmt =
            timeProps.time_format === "24_hour" ? "hh:mm A" : "HH:mm";

          const parse = (v) => {
            const primary = dayjs(v, timeFmt, true);
            if (primary.isValid()) return primary;
            return dayjs(v, altFmt, true);
          };

          validator = validator.test(
            "time-dependency",
            // FIX 2: non-empty fallback message — createError will override it
            "Invalid time comparison",
            buildDependencyTest(parse, dependency),
          );
        }
      }

      // ── Input Date — dependency ──────────────────────────────────────────
      if (field_type === "Input Date") {
        const dateProps = property.input_date || {};
        const dependency = dateProps.dependency || {};

        if (dependency.dependency_uuid && dependency.type) {
          // Values are stored as ISO "YYYY-MM-DD" by the onChange handler.
          // FIX 3: Also accept "DD/MM/YY" display format as fallback in case
          // the value has not yet been normalised to ISO.
          const parse = (v) => {
            const iso = dayjs(v, "YYYY-MM-DD", true);
            if (iso.isValid()) return iso.startOf("day");
            const display = dayjs(v, "DD/MM/YY", true);
            if (display.isValid()) return display.startOf("day");
            return dayjs(v).startOf("day"); // last-resort lenient parse
          };

          validator = validator.test(
            "date-dependency",
            // FIX 2: non-empty fallback message
            "Invalid date comparison",
            buildDependencyTest(parse, dependency),
          );
        }
      }
      // ── Input Date and Time — dependency ─────────────────────────────────
      if (field_type === "Input Date and Time") {
        const dateTimeProps = property.input_date_and_time || {};
        const dependency = dateTimeProps.dependency || {};

        if (dependency?.dependency_uuid && dependency?.type) {
          // Mirror the format that FormField/ValidationAndSubmit uses when
          // storing the value via DateTimePicker onChange:
          //   format = `${normalizeDateFormat(date_display_format)} ${timeFormate}`
          // normalizeDateFormat keeps "/" separators intact for "DD/MM/YY" style
          // formats, so we reproduce the same logic here.
          const rawDateFmt = dateTimeProps.date_display_format || "DD/MM/YY";
          // Replicate normalizeDateFormat: replace "-" → "/", collapse spaces
          const datePart = rawDateFmt
            .replace(/-/g, "/")
            .replace(/\s+/g, "");

          // Primary time format from the field config
          const isPrimary24h = dateTimeProps.time_format === "24_hour";
          const timePart = isPrimary24h ? "HH:mm" : "hh:mm A";
          const altTimePart = isPrimary24h ? "hh:mm A" : "HH:mm";

          // Build both candidate format strings so we can try the alternate
          // time format as a fallback (guards against mis-stored values).
          const dtFormat = `${datePart} ${timePart}`;
          const dtFormatAlt = `${datePart} ${altTimePart}`;

          /**
           * Parse a datetime string using the primary format, then the
           * alternate time-format variant, then a last-resort lenient parse.
           */
          const parseDT = (v) => {
            if (!v) return null;
            let d = dayjs(v, dtFormat, true);
            if (d.isValid()) return d;
            d = dayjs(v, dtFormatAlt, true);
            if (d.isValid()) return d;
            // Last-resort: let dayjs figure it out (handles ISO strings etc.)
            d = dayjs(v);
            return d.isValid() ? d : null;
          };

          validator = validator.test(
            "date-time-dependency",
            "Invalid datetime dependency",
            function (value) {
              if (!value) return true;

              const current = parseDT(value);
              if (!current || !current.isValid()) return true;

              const dependentValue = this.parent[dependency.dependency_uuid];
              if (!dependentValue) return true;

              const other = parseDT(dependentValue);
              if (!other || !other.isValid()) return true;

              const label =
                dependency.dependency_label || "the dependent field";

              if (isGreaterType(dependency.type)) {
                if (!current.isAfter(other)) {
                  return this.createError({
                    message: `Date and Time must be greater than ${label}`,
                  });
                }
              }

              if (isLessType(dependency.type)) {
                if (!current.isBefore(other)) {
                  return this.createError({
                    message: `Date and Time must be less than ${label}`,
                  });
                }
              }

              return true;
            },
          );
        }
      }

      schemaObject[input_uuid] = validator;
    });
  });

  return yup.object().shape(schemaObject);
};